import { useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import {
	LeadEntity,
	LeadSearchParams,
	PaginationOptions,
	SalesforcePicklist,
} from 'src/api';
import { LeadStatusEnum } from 'src/api/models/Enums';
import { useQuery } from '@tanstack/react-query';
import { useApiClient } from 'src/hooks/useApiClient';
import {
	translateLeadMotivation,
	translateSfAccountsTypes,
} from 'src/utils/translations';

const cleanQueryParams = (queryParams: any) => {
	return _.omitBy(
		queryParams,
		(value: any) => _.isNil(value) || (_.isString(value) && _.isEmpty(value))
	);
};

const countActiveFilters = (queryParams: any) => {
	const mapped = _.omit(mapQueryParams(queryParams), [
		'leadIds',
		'orderBy',
		'ascending',
		'skip',
		'limit',
		'status',
	]);
	return Object.keys(mapped).length;
};

const mapQueryParams = (queryParams: any) => {
	const createdDateFrom = queryParams?.CreatedDate?.from;
	const createdDateTo = queryParams?.CreatedDate?.to;

	const mappedParams: Partial<LeadSearchParams> = {
		leadIds: queryParams?.leadIds?.length ? queryParams?.leadIds : undefined,
		orderBy: queryParams?.orderBy,
		ascending: queryParams?.ascending,
		skip: queryParams?.skip,
		limit: queryParams?.limit,
		firstName: queryParams?.FirstName,
		lastName: queryParams?.LastName,
		email: queryParams?.Email,
		phone: queryParams?.Phone,
		codiceFiscale: queryParams?.Codice_Fiscale__c,
		partitaIva: queryParams?.Partita_Iva__c,
		ragioneSociale: queryParams?.Company,
		createdDateFrom: createdDateFrom
			? new Date(createdDateFrom).toISOString()
			: undefined,
		createdDateTo: createdDateTo
			? new Date(createdDateTo).toISOString()
			: undefined,
		city: queryParams?.City,
		salesUser: queryParams?.salesUser,
		leadSource: queryParams?.LeadSource,
		marca: queryParams?.Marca__c,
		modello: queryParams?.Modello__c,
		status: queryParams?.status,
		filialeDealerTitolare: queryParams?.Filiale_Dealer_Titolare__c,
	};

	return cleanQueryParams(mappedParams);
};

const DEFAULT_PAGINATION: PaginationOptions = {
	skip: 0,
	limit: 50,
} as const;

const INIT_FILTER_QUERY: LeadSearchParams = Object.assign(
	{},
	{
		...DEFAULT_PAGINATION,
		status: LeadStatusEnum.NEW,
	}
);
type UseLeadsParams = Partial<LeadSearchParams> & {
	enabled?: boolean;
};

type LeadsResponse = {
	data: LeadEntity[];
	totalCount: number;
};

type Updater<T> = T | ((prev: T) => Partial<T>);

function useLeadsPagedQuery(
	searchParams: Partial<LeadSearchParams>,
	options: { enabled?: boolean } = {}
) {
	const { apiClient } = useApiClient();

	return useQuery<LeadsResponse>({
		queryKey: ['leads_list', searchParams],
		queryFn: async () => {
			const params = mapQueryParams(searchParams);
			return apiClient.leadsControllerService.pagedQuery(params);
		},

		enabled: options.enabled ?? true,
		refetchOnWindowFocus: false,
		retry: false,
	});
}

export function useLeadFields() {
	const { apiClient } = useApiClient();

	const { data, isLoading, error } = useQuery<SalesforcePicklist[]>({
		queryKey: ['lead_list_fields'],
		queryFn: async () => {
			return apiClient.leadsControllerService.getLeadFields();
		},
		refetchOnWindowFocus: false,
		retry: false,
	});

	const leadSourceSelectOptions = useMemo(() => {
		const field = (data || []).find((item) => item.name === 'LeadSource');

		if (!field) return [];

		return (field.picklistValues || [])
			.filter((item) => item.active)
			.map((item) => item);
	}, [data]);

	const leadMotivationSelectOptions = useMemo(() => {
		const field = (data || []).find((item) => item.name === 'Motivazione__c');

		if (!field) return [];

		return (field.picklistValues || [])
			.filter((item) => item.active)
			.map((item) => ({ ...item, label: translateLeadMotivation(item.label) }));
	}, [data]);

	const leadClientTypeSelectOptions = useMemo(() => {
		const field = (data || []).find((item) => item.name === 'Cliente__c');

		if (!field) return [];

		return (field.picklistValues || [])
			.filter((item) => item.active)
			.map((item) => ({
				...item,
				label: translateSfAccountsTypes(item.label),
			}));
	}, [data]);

	return {
		isLoadingFields: isLoading,
		fieldsError: error,
		leadSourceSelectOptions,
		leadMotivationSelectOptions,
		leadClientTypeSelectOptions,
	};
}

export function useLeads(params: UseLeadsParams) {
	const { status, enabled, ...initialFilters } = params;
	const [response, setResponse] = useState<LeadsResponse | undefined>(
		undefined
	);

	const [filterQuery, setFilterQuery] = useState<LeadSearchParams>(() => {
		return cleanQueryParams({
			...INIT_FILTER_QUERY,
			...initialFilters,
			...(status && { status }),
		}) as LeadSearchParams;
	});

	const { data, isLoading, error, refetch } = useLeadsPagedQuery(filterQuery, {
		enabled,
	});

	useEffect(() => {
		data && setResponse(data);
	}, [data]);

	const resetFilter = useCallback(
		(resetParams: Partial<LeadSearchParams> = {}) => {
			const newFilter = {
				...INIT_FILTER_QUERY,
				...resetParams,
			};
			setFilterQuery(cleanQueryParams(newFilter) as LeadSearchParams);
		},
		[]
	);

	const leads = useMemo(() => {
		if (!response) return [];
		return Array.isArray(response.data) ? response.data : [];
	}, [response]);

	const findById = useCallback(
		(id?: string): LeadEntity | undefined => {
			if (!id || !leads.length) return undefined;
			return leads.find((lead) => lead?.Id === id);
		},
		[leads]
	);

	const updateLeadsFilter = useCallback(
		(updater: Updater<LeadSearchParams>) => {
			setFilterQuery((prev) => {
				let updates: Partial<LeadSearchParams>;

				if (typeof updater === 'function') {
					updates = updater(prev);
				} else {
					updates = updater;
				}

				const newFilter = { ...prev, ...updates };

				const isPaginationOnlyUpdate = Object.keys(updates).every((key) =>
					['skip', 'limit'].includes(key)
				);

				if (!isPaginationOnlyUpdate && updates.skip === undefined) {
					newFilter.skip = 0;
				}

				return cleanQueryParams(newFilter) as LeadSearchParams;
			});
		},
		[]
	);

	const setSortBy = useCallback(
		(orderBy: string, order: 'ASC' | 'DESC' = 'DESC') => {
			// @ts-ignore
			updateLeadsFilter({ orderBy, order });
		},
		[updateLeadsFilter]
	);

	const fetchAllLeads = async (
		apiCall: (params: any) => Promise<any>,
		queryParams: Record<string, any> = {},
		batchSize: number = 100
	): Promise<LeadEntity[]> => {
		const allData: LeadEntity[] = [];
		let skip = 0;
		let totalCount = 0;

		do {
			try {
				const response = await apiCall({
					...mapQueryParams(queryParams),
					skip,
					limit: batchSize,
				});
				allData.push(...response.data);
				totalCount = response.totalCount;
				skip += batchSize;

				if (allData.length >= totalCount || response.data.length === 0) {
					break;
				}
			} catch (error) {
				console.error(`Error fetching batch starting at ${skip}:`, error);
				throw error;
			}
		} while (allData.length < totalCount);

		return allData;
	};

	const activeFiltersCount = useMemo(() => {
		return countActiveFilters(filterQuery);
	}, [filterQuery]);

	return {
		leads,
		totalCount: response?.totalCount,
		isLoadingLeads: isLoading,
		leadsError: error,
		findById,
		resetFilter,
		updateLeadsFilter,
		refetchLeads: refetch,
		currentLeadsFilter: filterQuery,
		setLeadsSortBy: setSortBy,
		fetchAllLeads,
		activeFiltersCount,
	};
}
